if (!customElements.get('roomvo-buttons')) {
  customElements.define(
    'roomvo-buttons',
    class RoomvoButtons extends HTMLElement {
      static SAUCE_VISIBILITY_DELAYS_MS = [0, 500, 1000, 3000, 6000];
      static SCROLL_TOP_OFFSET_PX = 10;
      static SAUCE_SECTION_SELECTOR = '[data-sauce-section-id], [data-sauce-scroll-target]';
      static MOBILE_MEDIA_QUERY = '(max-width: 767px)';

      constructor() {
        super();
        this.observer = null;
        this.refreshTimeouts = [];
        this.sauceCheckTimeouts = [];
        this.sauceSectionObserver = null;
        this.sauceVisibilityTimeout = null;
        this.onPageLoad = () => this.updateSauceButtonsVisibility();
        this.onMainContentMutation = () => this.scheduleSauceVisibilityCheck();
      }

      connectedCallback() {
        this.initSauceScrollButtons();
        this.initRoomvoButtons();

        if (!this.querySelector(RoomvoButtons.SAUCE_SECTION_SELECTOR)) {
          this.scheduleInspirationPanelUpdates();
        }
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
          this.observer = null;
        }

        this.refreshTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
        this.refreshTimeouts = [];

        this.sauceCheckTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
        this.sauceCheckTimeouts = [];

        if (this.sauceSectionObserver) {
          this.sauceSectionObserver.disconnect();
          this.sauceSectionObserver = null;
        }

        clearTimeout(this.sauceVisibilityTimeout);

        window.removeEventListener('load', this.onPageLoad);
      }

      initRoomvoButtons() {
        const buttons = this.querySelectorAll('a.roomvo-stimr, a.roomvo-product-display-button');
        if (buttons.length === 0) return;

        this.checkRoomvoVisibility();

        const config = { attributes: true, attributeFilter: ['style', 'class'], subtree: true };
        this.observer = new MutationObserver(() => this.checkRoomvoVisibility());
        this.observer.observe(this, config);

        const nudgeRoomvo = () => {
          if (window.roomvo && typeof window.roomvo.refreshElements === 'function') {
            window.roomvo.refreshElements(this);
          }
        };

        nudgeRoomvo();
        this.refreshTimeouts.push(setTimeout(nudgeRoomvo, 1000));
        this.refreshTimeouts.push(setTimeout(nudgeRoomvo, 3000));
      }

      initSauceScrollButtons() {
        if (!this.querySelector(RoomvoButtons.SAUCE_SECTION_SELECTOR)) {
          return;
        }

        RoomvoButtons.bindDocumentSauceClick();

        window.addEventListener('load', this.onPageLoad);

        const mainContent = document.querySelector('#MainContent');
        if (mainContent) {
          this.sauceSectionObserver = new MutationObserver(this.onMainContentMutation);
          this.sauceSectionObserver.observe(mainContent, { childList: true, subtree: true });
        }

        RoomvoButtons.SAUCE_VISIBILITY_DELAYS_MS.forEach((delay) => {
          this.sauceCheckTimeouts.push(setTimeout(() => this.updateSauceButtonsVisibility(), delay));
        });

        this.updateSauceButtonsVisibility();
      }

      scheduleInspirationPanelUpdates() {
        RoomvoButtons.SAUCE_VISIBILITY_DELAYS_MS.forEach((delay) => {
          this.sauceCheckTimeouts.push(setTimeout(() => this.updateInspirationPanels(), delay));
        });

        this.updateInspirationPanels();
      }

      scheduleSauceVisibilityCheck() {
        clearTimeout(this.sauceVisibilityTimeout);
        this.sauceVisibilityTimeout = setTimeout(() => this.updateSauceButtonsVisibility(), 150);
      }

      getSauceSectionId(button) {
        return (
          button.getAttribute('data-sauce-section-id') ||
          button.getAttribute('data-sauce-scroll-target') ||
          ''
        ).trim();
      }

      static bindDocumentSauceClick() {
        if (RoomvoButtons.documentSauceClickBound) {
          return;
        }

        RoomvoButtons.documentSauceClickBound = true;

        document.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest(RoomvoButtons.SAUCE_SECTION_SELECTOR);

            if (!button || button.hidden) {
              return;
            }

            const host = button.closest('roomvo-buttons');

            if (!host) {
              return;
            }

            const sectionKey = host.getSauceSectionId(button);
            const section = host.findScrollSection(sectionKey);

            if (!section) {
              return;
            }

            event.preventDefault();
            host.scrollToSection(section);
          },
          true,
        );
      }

      getMainContent() {
        return document.querySelector('#MainContent');
      }

      findScrollSection(sectionKey) {
        const key = sectionKey.replace(/^shopify-section-/, '');

        if (!key) {
          return null;
        }

        const main = this.getMainContent();

        if (!main) {
          return null;
        }

        const escapedKey = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(key) : key;
        const section =
          main.querySelector(`#shopify-section-${escapedKey}`) ||
          main.querySelector(`.shopify-section[id$="__${escapedKey}"]`);

        if (!section?.isConnected) return null;
        return section.offsetHeight > 0 ? section : null;
      }

      getElementDocumentTop(element) {
        const rect = element.getBoundingClientRect();
        const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;

        return rect.top + scrollY;
      }

      scrollToSection(section) {
        const headerOffset = this.getHeaderOffset();
        const isMobile = window.matchMedia(RoomvoButtons.MOBILE_MEDIA_QUERY).matches;
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth';

        const scrollToPosition = () => {
          const maxScroll =
            document.documentElement.scrollHeight - window.innerHeight;
          const top = this.getElementDocumentTop(section) - headerOffset;
          const targetTop = Math.max(0, Math.min(Math.round(top), maxScroll));

          window.scrollTo({
            top: targetTop,
            left: 0,
            behavior,
          });
        };

        section.style.setProperty('scroll-margin-top', `${headerOffset}px`);
        scrollToPosition();

        if (isMobile) {
          window.requestAnimationFrame(scrollToPosition);
          window.setTimeout(scrollToPosition, 150);
        }
      }

      getHeaderOffset() {
        const mainContent = document.querySelector('#MainContent');
        const mainStyles = mainContent ? getComputedStyle(mainContent) : null;
        const fromMain = Number.parseFloat(mainStyles?.getPropertyValue('--header-height')) || 0;
        const measured = this.measureHeaderHeight();

        return Math.max(fromMain, measured) + RoomvoButtons.SCROLL_TOP_OFFSET_PX;
      }

      measureHeaderHeight() {
        const announcement = document.querySelector('.shopify-section-group-header-group .announcement-bar');
        const header = document.querySelector('.shopify-section-group-header-group #SiteHeader');

        return (announcement?.offsetHeight || 0) + (header?.offsetHeight || 0);
      }

      updateSauceButtonsVisibility() {
        document.querySelectorAll(RoomvoButtons.SAUCE_SECTION_SELECTOR).forEach((button) => {
          const hasSection = Boolean(this.findScrollSection(this.getSauceSectionId(button)));
          button.hidden = !hasSection;
        });

        this.updateInspirationPanels();
      }

      isRoomvoButtonVisible(button) {
        const isVisible =
          button.style.visibility === 'visible' || getComputedStyle(button).visibility === 'visible';
        const hasHiddenAttr = button.getAttribute('style')?.includes('visibility: hidden');

        return isVisible && !hasHiddenAttr;
      }

      getVisibleActionCount(container) {
        const sauceCount = container.querySelectorAll(
          '.product-details__action-button--sauce:not([hidden])',
        ).length;

        let roomvoCount = 0;

        container.querySelectorAll('a.roomvo-stimr, a.roomvo-product-display-button').forEach((button) => {
          if (this.isRoomvoButtonVisible(button)) {
            roomvoCount++;
          }
        });

        return sauceCount + roomvoCount;
      }

      updateInspirationPanels() {
        document.querySelectorAll('.product-media-gallery__panel--inspiration').forEach((panel) => {
          const isEmpty = this.getVisibleActionCount(panel) === 0;

          panel.classList.toggle('is-empty', isEmpty);
          panel.hidden = isEmpty;

          const heading = panel.querySelector('.product-media-gallery__panel-heading');

          if (heading) {
            heading.hidden = isEmpty;
          }
        });

        this.syncGalleryPanelLayout();

        const mobileInspirationRow = document.querySelector('.product-details__actions--inspiration');

        if (mobileInspirationRow) {
          mobileInspirationRow.hidden = this.getVisibleActionCount(mobileInspirationRow) === 0;
        }
      }

      syncGalleryPanelLayout() {
        const gallery = document.querySelector('.product-media-gallery[data-product-images]');

        if (!gallery) {
          return;
        }

        const inspirationPanel = gallery.querySelector('.product-media-gallery__panel--inspiration');
        const isInspirationEmpty =
          !inspirationPanel ||
          inspirationPanel.hidden ||
          inspirationPanel.classList.contains('is-empty');

        gallery.classList.toggle('product-media-gallery--visualise-span-2', isInspirationEmpty);
      }

      checkRoomvoVisibility() {
        const buttons = this.querySelectorAll('a.roomvo-stimr, a.roomvo-product-display-button');
        let visibleCount = 0;
        const panel = this.closest('[data-roomvo-panel]');

        buttons.forEach((btn) => {
          if (this.isRoomvoButtonVisible(btn)) {
            visibleCount++;
          }
        });

        this.classList.toggle('has-single-button', visibleCount === 1);
        this.classList.toggle('is-ready', visibleCount > 0);

        if (panel) {
          panel.hidden = visibleCount === 0;
        }

        if (this.closest('.product-media-gallery__panel--inspiration, .product-details__actions--inspiration')) {
          this.updateInspirationPanels();
        }
      }
    },
  );

  customElements.get('roomvo-buttons').bindDocumentSauceClick();
}
